import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery } from 'convex/react';
import { DMReport, adminSession } from '@/lib/api';
import { api } from '../../../convex/functions/_generated/api';
import type { Id } from '../../../convex/functions/_generated/dataModel';
import { friendlyError } from '@/lib/errors';

/**
 * Admin queue for DM abuse reports. Wraps GET /admin/dm/reports +
 * POST /admin/dm/reports/{id}/resolve. Sorted newest-first by the
 * backend; we just render the list + inline-resolve.
 *
 * Resolution choices follow the backend ResolveReport contract:
 *   - "reviewed"  — confirmed abuse, action taken offline (we don't
 *                   surface a suspend button here yet; the moderator
 *                   actions via the Users page or psql)
 *   - "dismissed" — false alarm, no action
 *
 * Optional admin_notes is a free-text rationale stored on the row.
 */
export function DMReportsPanel() {
  const token = adminSession.getToken() ?? '';
  const data = useQuery(api.admin.reports.listDMReports, token ? { token } : 'skip');
  const resolveMut = useMutation(api.admin.reports.resolveDMReport);
  const reports = (data?.reports ?? null) as DMReport[] | null;
  const loading = data === undefined;
  const [err, setErr] = useState<string | null>(null);
  // Per-row in-flight state: which report id is being resolved and which
  // resolution (so we can swap the button labels independently).
  const [resolving, setResolving] = useState<{ id: string; res: 'reviewed' | 'dismissed' } | null>(null);
  // Per-row notes drafts. Keyed by report id; persists until row is
  // resolved + removed from the list.
  const [notes, setNotes] = useState<Record<string, string>>({});
  const [toast, setToast] = useState<string | null>(null);

  const resolve = async (id: string, res: 'reviewed' | 'dismissed') => {
    if (resolving) return;
    setResolving({ id, res });
    setErr(null);
    try {
      await resolveMut({
        token,
        id: id as Id<'dmReports'>,
        resolution: res,
        notes: notes[id] ?? '',
      });
      // Reactivity will drop the row from the queue on the next push
      // (backend filters to status='open'); clear the local notes draft.
      setNotes((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
      setToast(res === 'reviewed' ? 'Marked reviewed.' : 'Dismissed.');
      setTimeout(() => setToast(null), 2500);
    } catch (e) {
      setErr(friendlyError(e, 'DMReports'));
    } finally {
      setResolving(null);
    }
  };

  return (
    <div>
      <p style={{ color: 'var(--ink-3)', fontSize: 13, marginBottom: 24 }}>
        Open abuse reports from DM threads. Resolving removes the row from this queue (status flips on the backend).
      </p>

      {err && (
        <div style={{ background: 'rgba(208,64,48,0.08)', color: '#a8331f', padding: '10px 14px', borderRadius: 12, fontSize: 13, marginBottom: 16 }}>
          {err}
        </div>
      )}
      {toast && (
        <div style={{ background: 'rgba(56,142,60,0.10)', color: '#2e7d32', padding: '10px 14px', borderRadius: 12, fontSize: 13, marginBottom: 16 }}>
          {toast}
        </div>
      )}

      {loading || !reports ? (
        <div style={{ padding: 24, color: 'var(--ink-3)', fontSize: 13 }}>Loading…</div>
      ) : reports.length === 0 ? (
        <div style={{
          padding: 32,
          color: 'var(--ink-3)',
          fontSize: 14,
          textAlign: 'center',
          background: 'var(--surface)',
          borderRadius: 14,
          border: '1px solid var(--line)' }}>
          No open reports. Nice and quiet.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {reports.map((r) => (
            <ReportCard
              key={r.id}
              report={r}
              notes={notes[r.id] ?? ''}
              onNotesChange={(v) => setNotes((prev) => ({ ...prev, [r.id]: v }))}
              busy={resolving?.id === r.id ? resolving.res : null}
              onResolve={(res) => resolve(r.id, res)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function ReportCard({
  report,
  notes,
  onNotesChange,
  busy,
  onResolve }: {
  report: DMReport;
  notes: string;
  onNotesChange: (v: string) => void;
  busy: 'reviewed' | 'dismissed' | null;
  onResolve: (r: 'reviewed' | 'dismissed') => void;
}) {
  return (
    <div style={{
      background: 'var(--surface)',
      border: '1px solid var(--line)',
      borderRadius: 14,
      padding: 18,
      display: 'flex',
      flexDirection: 'column',
      gap: 12 }}>
      {/* Header — meta row */}
      <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'baseline', gap: 12 }}>
        <div className="mono" style={{ fontSize: 11, color: 'var(--ink-3)', letterSpacing: 0.6 }}>
          REPORT · {new Date(report.created_at).toLocaleString()}
        </div>
        <div className="mono" style={{ fontSize: 10, color: 'var(--ink-3)', letterSpacing: 0.6 }}>
          {report.id.slice(0, 8)}
        </div>
      </div>

      {/* User ids — uuid for now; can join to display_name later via a
          /admin/users/{id}/quick endpoint if the queue ever has any volume. */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 18, fontSize: 13, color: 'var(--ink-2)' }}>
        <div style={{ minWidth: 0, overflowWrap: 'anywhere' }}>
          <span className="mono" style={{ fontSize: 10, color: 'var(--ink-3)', letterSpacing: 0.6 }}>REPORTER</span><br />
          <Link to={`/dashboard/users/${report.reporter_user_id}`} style={uuidLink} title="View user in admin">{report.reporter_user_id}</Link>
        </div>
        <div style={{ minWidth: 0, overflowWrap: 'anywhere' }}>
          <span className="mono" style={{ fontSize: 10, color: 'var(--ink-3)', letterSpacing: 0.6 }}>REPORTED</span><br />
          <Link to={`/dashboard/users/${report.reported_user_id}`} style={uuidLink} title="View user in admin">{report.reported_user_id}</Link>
        </div>
      </div>

      {/* Reason — reporter's free text. Max 500 chars server-side. */}
      <div>
        <div className="mono" style={{ fontSize: 10, color: 'var(--ink-3)', letterSpacing: 0.6, marginBottom: 4 }}>REASON</div>
        <div style={{ fontSize: 14, color: 'var(--ink)', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{report.reason}</div>
      </div>

      {/* Offending message — present when message_id was supplied (always
          in v1; the backend rejects message-less reports). */}
      {report.message_body ? (
        <div>
          <div className="mono" style={{ fontSize: 10, color: 'var(--ink-3)', letterSpacing: 0.6, marginBottom: 4 }}>OFFENDING MESSAGE</div>
          <blockquote style={{
            margin: 0,
            padding: '10px 14px',
            background: 'var(--bg)',
            borderLeft: '3px solid var(--coral)',
            borderRadius: 6,
            fontSize: 14,
            color: 'var(--ink)',
            whiteSpace: 'pre-wrap',
            overflowWrap: 'anywhere' }}>
            {report.message_body}
          </blockquote>
        </div>
      ) : (
        <div className="mono" style={{ fontSize: 11, color: 'var(--ink-3)' }}>
          (Message was deleted or not attached.)
        </div>
      )}

      {/* Admin notes — optional free text recorded on resolve. */}
      <div>
        <label className="mono" htmlFor={`notes-${report.id}`} style={{ fontSize: 10, color: 'var(--ink-3)', letterSpacing: 0.6, marginBottom: 4, display: 'block' }}>
          ADMIN NOTES (optional)
        </label>
        <textarea
          id={`notes-${report.id}`}
          value={notes}
          onChange={(e) => onNotesChange(e.target.value)}
          placeholder="Internal rationale; not shown to either user."
          rows={2}
          disabled={busy !== null}
          style={{
            width: '100%',
            padding: '8px 10px',
            border: '1px solid var(--line)',
            borderRadius: 8,
            fontSize: 13,
            fontFamily: 'var(--font-ui)',
            background: 'var(--bg)',
            color: 'var(--ink)',
            resize: 'vertical' }}
        />
      </div>

      {/* Action row */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginTop: 4 }}>
        <button
          type="button"
          onClick={() => onResolve('reviewed')}
          disabled={busy !== null}
          style={{
            padding: '8px 16px',
            background: 'var(--coral)',
            color: '#fff',
            border: 'none',
            borderRadius: 8,
            fontSize: 13,
            fontWeight: 600,
            cursor: busy ? 'default' : 'pointer',
            opacity: busy && busy !== 'reviewed' ? 0.5 : 1,
            fontFamily: 'var(--font-ui)' }}
        >
          {busy === 'reviewed' ? 'Saving…' : 'Mark reviewed'}
        </button>
        <button
          type="button"
          onClick={() => onResolve('dismissed')}
          disabled={busy !== null}
          style={{
            padding: '8px 16px',
            background: 'transparent',
            color: 'var(--ink-2)',
            border: '1px solid var(--line-strong)',
            borderRadius: 8,
            fontSize: 13,
            fontWeight: 600,
            cursor: busy ? 'default' : 'pointer',
            opacity: busy && busy !== 'dismissed' ? 0.5 : 1,
            fontFamily: 'var(--font-ui)' }}
        >
          {busy === 'dismissed' ? 'Saving…' : 'Dismiss'}
        </button>
      </div>
    </div>
  );
}

const uuidLink: React.CSSProperties = {
  fontFamily: 'var(--font-mono)',
  fontSize: 12,
  color: 'var(--ink)',
  textDecoration: 'none',
  borderBottom: '1px dashed var(--line-strong)',
};
